import { comparer, computed, IComputedValue, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { ActionType, Pane, PaneType, PaneTypeNames } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { SelectItem, SelectRoot } from "../ui/Select";
import { Tooltip } from "../ui/Tooltip";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  functionPaneHighlightIdBox,
  selectedPaneIdsAtom,
} from "./CanvasState";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { zoomToSelection } from "./ToolbarZoomButton";

const NO_PANE_SENTINEL = "undefined";
const PaneSelector = observer(
  ({
    paneIds,
    value,
    onChange,
  }: {
    paneIds: string[];
    value: string | undefined;
    onChange: (paneId: string | undefined) => void;
  }) => {
    const paneOptions = computed(
      () =>
        paneIds.flatMap((paneId) => {
          const pane = canvasAtom.value.panes.find((p) => p.id === paneId);
          if (pane === undefined) {
            return [];
          }
          return [
            [
              pane.id,
              pane.name ??
                (pane.type === PaneType.Evaluate
                  ? pane.expression.substring(0, 16)
                  : PaneTypeNames[pane.type]),
            ],
          ] as const;
        }),
      { equals: comparer.structural }
    ).get();
    return (
      <SelectRoot
        value={value ?? NO_PANE_SENTINEL}
        onChange={(v) => {
          onChange(v === NO_PANE_SENTINEL ? undefined : v);
        }}
        className="font-mono-natto w-full flex justify-between"
        contentClassName="font-mono-natto"
      >
        <SelectItem
          value={NO_PANE_SENTINEL}
          text="none"
          className="text-opacity-50"
        />
        {paneOptions.map(([paneId, paneName]) => (
          <SelectItem value={paneId} text={paneName} key={paneId} />
        ))}
      </SelectRoot>
    );
  }
);

export const PaneFunctionComponent = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    focusOnMount,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Function }> | undefined
    >;
    isMaximized: boolean;
    focusOnMount: boolean;
  }) => {
    const activePaneState = activePaneStates.get(paneId)!;
    const viewMode = activePaneState.viewMode.get();
    const output = activePaneState.outputBoxes[0].get();
    const paneIds = computed(() => paneComputed.get()?.paneIds).get() ?? [];
    const inputPaneId = computed(() => paneComputed.get()?.inputPaneId).get();
    const outputPaneId = computed(() => paneComputed.get()?.outputPaneId).get();

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          <div className="grow" />
          {!isMaximized ? (
            <PaneTransformButton paneId={paneId} outputIndex={0} />
          ) : null}
        </PaneHeaderBar>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <div className="px-2 py-1 text-neutral-400 flex items-center text-2xs bg-neutral-100">
            <FeatherIcon
              icon="alert-triangle"
              size="sm"
              className="text-neutral-300 mr-1.5"
            />
            This is an experimental pane.
          </div>
          <div className="p-2 overflow-auto">
            <div className="text-neutral-400 mb-2.5">
              async function running{" "}
              <button
                onMouseEnter={() => {
                  runInAction(() => {
                    functionPaneHighlightIdBox.set(paneId);
                  });
                }}
                onMouseLeave={() => {
                  runInAction(() => {
                    if (functionPaneHighlightIdBox.get() === paneId) {
                      functionPaneHighlightIdBox.set(undefined);
                    }
                  });
                }}
                onClick={() => {
                  runInAction(() => {
                    selectedPaneIdsAtom.value = paneIds;
                    zoomToSelection();
                  });
                }}
                className="text-primary-500"
              >
                {paneIds.length} pane
                {paneIds.length !== 1 ? "s" : ""}
              </button>
            </div>
            {output[0] === "error" ? (
              <div>
                <div className="text-red-500">{output[1]}</div>
              </div>
            ) : (
              <>
                <div className="flex items-center mb-2">
                  <label className="font-medium mr-1">input</label>
                  <Tooltip
                    content={
                      "function argument will be\nused as this pane's value"
                    }
                  >
                    <FeatherIcon
                      icon="help-circle"
                      size="sm"
                      className="text-neutral-300"
                    />
                  </Tooltip>
                  <div className="w-8" />
                  <PaneSelector
                    paneIds={paneIds}
                    value={inputPaneId}
                    onChange={(inputPaneId) => {
                      canvasDispatch([
                        ActionType.SetFunctionPaneInputPaneId,
                        { paneId, inputPaneId },
                      ]);
                    }}
                  />
                </div>
                <div className="flex items-center">
                  <label className="font-medium mr-1">output</label>
                  <Tooltip
                    content={"this is the return value\nof the function"}
                  >
                    <FeatherIcon
                      icon="help-circle"
                      size="sm"
                      className="text-neutral-300"
                    />
                  </Tooltip>
                  <div className="w-8" />
                  <PaneSelector
                    paneIds={paneIds}
                    value={outputPaneId}
                    onChange={(outputPaneId) => {
                      canvasDispatch([
                        ActionType.SetFunctionPaneOutputPaneId,
                        { paneId, outputPaneId },
                      ]);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </PaneBody>
      </>
    );
  }
);
